import React, { useState, useRef, useEffect, useMemo, useCallback, useContext } from "react";
import { flushSync } from "react-dom";
import { Upload, AlertCircle, CheckCircle, LogOut, ArrowLeft, Menu, Play, Eye, Volume2, Check, X, PanelLeft, Aperture, FilePenLine, Search, Images, Settings, ArrowRight, ChevronDown, FolderPlus, Plus, User, HelpCircle, Video } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { useNavigate, useLocation as useRouterLocation } from "react-router-dom";
import { API_ROOT, Project, projectAPI } from "@/config";
import SEO from "@/components/seo";
import AnalysisSidebar from "@/components/dashboard";
import ProjectModal from "@/components/ui/project-modal";
import CollapsedProfile from "@/components/ui/collapsed-profile";
import FeatureRequestButton from "@/components/ui/feature-request-button";
import './video-uploader.css';
import Shepherd from 'shepherd.js';
import 'shepherd.js/dist/css/shepherd.css';
import { createCreatorUploadTour, tourStyles } from "@/components/ui/tour-utils";

// Helper to get the correct path based on user type
const getBasePath = () => {
  const userType = localStorage.getItem("userType");
  return userType === 'brand' ? '/brand' : '';
};

interface AnalysisResult {
  custom_brand_identification: boolean;
  custom_brand_proportion: number;
  custom_brand_first_appearance: number;
  custom_product_detection: boolean;
  custom_product_proportion: number;
  custom_product_first_appearance: number;
  transcription: string;
  complies_with_dos: boolean;
  complies_with_donts: boolean;
  first_dos_timestamp: number;
  first_donts_timestamp: number;
  complies_with_dos_heard: boolean;
  complies_with_donts_heard: boolean;
  contains_sensitive_content: boolean;
  total_processing_time: number;
  unique_id?: string;
  visual?: {
    dos: { input: string; complies: boolean }[];
    donts: { input: string; complies: boolean }[];
  };
  visible_brands?: string[];
  visible_text?: string[];
}

interface AnalysisPayload {
  brandName: string;
  exactProduct: string;
  dosSeen: string[];
  dosHeard: string[];
  dontsSeen: string[];
  dontsHeard: string[];
  video_path?: string;
  transcriptLanguage: string;
  campaignName: string;
}

const STEPS = [
  "Video Upload",
  "Campaign Details",
  "DOs & DON'Ts",
  "Compliance Check",
];

const PALETTE = {
  inchworm: '#B1FA63',
  gunmetal: '#243837',
  orange: '#FE7733',
  paleViolet: '#B2A1FF',
  brightSnow: '#FFFFFF',
  americanSilver: '#D1D1D1',
};

const FoldedSidebar = ({ onShowSidebar, onNewAnalysisClick }: { onShowSidebar: () => void; onNewAnalysisClick: (e: React.MouseEvent) => void; }) => {

  return (
    <div
      className="h-full flex flex-col"
      style={{ 
        background: PALETTE.brightSnow, 
        width: 56, 
        borderRight: `1px solid ${PALETTE.americanSilver}` 
      }}
    >
      {/* Fixed Top Section - matching expanded sidebar */}
      <div className="flex-shrink-0">
        {/* Header with logo - same position as expanded */}
        <div className="flex items-center justify-center px-4 py-3">
          <div
            onClick={onShowSidebar}
            title="Expand Sidebar"
            className="cursor-pointer"
          >
            <img 
              src="/brandcomply_logo_gunmetal.png" 
              alt="brandcomply logo" 
              className="h-6 w-6"
            />
          </div>
        </div>

        {/* New Analysis Button - matching position */}
        <div
          onClick={onNewAnalysisClick}
          className="flex items-center justify-center mx-4 my-3 px-3 py-2 rounded-lg cursor-pointer transition-all hover:opacity-80"
          style={{ 
            background: PALETTE.inchworm,
            color: PALETTE.gunmetal
          }}
          title="New Check"
        >
          <FilePenLine className="w-4 h-4 flex-shrink-0" />
        </div>


      </div>

      {/* Spacer to push profile to bottom */}
      <div className="flex-1"></div>

      {/* Fixed Bottom Section - Profile matching expanded sidebar */}
      <div className="flex-shrink-0">
        <div className="flex justify-center p-2">
          <CollapsedProfile className="shepherd-profile-target" />
        </div>
      </div>
    </div>
  );
};

const Index = () => {
  // Campaign Mode Detection - Initialize SYNCHRONOUSLY to avoid race condition with auth redirect
  // This MUST be determined before first render to prevent AnalysisSidebar from rendering and redirecting
  const [isCampaignMode, setIsCampaignMode] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('campaign') === 'true' || localStorage.getItem('campaign_mode') === 'true';
  });
  const [campaignUuid, setCampaignUuid] = useState<string | null>(null);
  
  // Creator identification for campaign mode
  const [creatorEmail, setCreatorEmail] = useState("");
  const [creatorName, setCreatorName] = useState("");
  
  const [file, setFile] = useState<File | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [progress, setProgress] = useState(0);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [timerProgress, setTimerProgress] = useState(0);
  const [brandName, setBrandName] = useState("");
  const [campaignBrand, setCampaignBrand] = useState("");
  const [exactProduct, setExactProduct] = useState("");
  const [campaignName, setCampaignName] = useState("");
  const [dosText, setDosText] = useState("");
  const [dontsText, setDontsText] = useState("");
  const [transcriptLanguage, setTranscriptLanguage] = useState("de");
  const [step, setStep] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const saved = localStorage.getItem("sidebarOpen");
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
  const [isVideoFullscreen, setIsVideoFullscreen] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const fullscreenVideoRef = useRef<HTMLVideoElement>(null);
  
  // Project-related state
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);

  // Move tourSteps inside component to access setSidebarOpen
  const tourSteps = [
    {
      id: 'welcome',
      text: 'This tour will guide you through the main features of brandcomply.',
      attachTo: { element: '.shepherd-help-btn', on: 'top' as const },
      buttons: [
        { text: 'Next', action: function() { (this as any).next(); } }
      ]
    },
    {
      id: 'sidebar-logo',
      text: 'Click the brandcomply logo to open the sidebar and access your projects and previous compliance checks.',
      attachTo: { element: 'img[alt="brandcomply logo"]', on: 'right' as const },
      buttons: [
        { text: 'Back', action: function() { (this as any).back(); } },
        { text: 'Next', action: function() { (this as any).next(); } }
      ]
    },
    {
      id: 'new-check',
      text: 'Click this button to start a new compliance check.',
      attachTo: { element: '[title="New Check"]', on: 'right' as const },
      buttons: [
        { text: 'Back', action: function() { (this as any).back(); } },
        { text: 'Next', action: function() { (this as any).next(); } }
      ]
    },
    {
      id: 'profile-circle',
      text: 'Click your profile circle to logout.',
      attachTo: { element: '.shepherd-profile-target', on: 'right' as const },
      buttons: [
        { text: 'Back', action: function() { (this as any).back(); } },
        { text: 'Next', action: function() { (this as any).next(); } }
      ]
    },
    {
      id: 'open-sidebar',
      text: 'Now let\'s open the sidebar to see more features.',
      attachTo: { element: 'img[alt="brandcomply logo"]', on: 'right' as const },
      buttons: [
        { text: 'Back', action: function() { (this as any).back(); } },
        { text: 'Open Sidebar', action: function() { 
          setSidebarOpen(true);
          // Add a delay to allow the sidebar to fully open before proceeding
          setTimeout(() => {
            (this as any).next(); 
          }, 300);
        } }
      ]
    },
    {
      id: 'hide-sidebar',
      text: 'Click this button to hide the sidebar and get more space for your work.',
      attachTo: { element: '.hide-sidebar-button', on: 'left' as const },
      beforeShowPromise: function() {
        // Wait for the sidebar to be fully rendered
        return new Promise(resolve => {
          setTimeout(() => {
            const element = document.querySelector('.hide-sidebar-button');
            console.log('Hide sidebar element found:', element);
            resolve(undefined);
          }, 100);
        });
      },
      buttons: [
        { text: 'Back', action: function() { 
          setSidebarOpen(false);
          (this as any).back(); 
        } },
        { text: 'Next', action: function() { (this as any).next(); } }
      ]
    },
    {
      id: 'new-project',
      text: 'Click this button to create a new project folder where you can organize individual compliance checks.',
      attachTo: { element: '.new-project-button', on: 'left' as const },
      buttons: [
        { text: 'Back', action: function() { (this as any).back(); } },
        { text: 'Next', action: function() { (this as any).next(); } }
      ]
    },
    {
      id: 'video-player',
      text: 'This is where you upload your video. You can drag and drop a video file or click "Choose File" to select one.',
      attachTo: { element: '[style*="aspect-ratio: 9 / 16"]', on: 'right' as const },
      buttons: [
        { text: 'Back', action: function() { (this as any).back(); } },
        { text: 'Next', action: function() { (this as any).next(); } }
      ]
    },
    {
      id: 'follow-process',
      text: 'Once you uploaded a video, follow the process steps by clicking this button to continue with your compliance check.',
      attachTo: { element: '.campaign-details-button', on: 'top' as const },
      buttons: [
        { text: 'Back', action: function() { (this as any).back(); } },
        { text: 'Done', action: function() { (this as any).complete(); } }
      ]
    }
  ];

  const navigate = useNavigate();
  const location = useRouterLocation();

  useEffect(() => {
    localStorage.setItem("sidebarOpen", JSON.stringify(sidebarOpen));
  }, [sidebarOpen]);

  // Load campaign data if in campaign mode
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const isCampaign = params.get('campaign') === 'true' || localStorage.getItem('campaign_mode') === 'true';
    
    if (isCampaign) {
      setIsCampaignMode(true);
      setStep(0); // Force to video upload step only
      const uuid = localStorage.getItem('campaign_uuid');
      const name = localStorage.getItem('campaign_name');
      const brand = localStorage.getItem('campaign_brand');
      const product = localStorage.getItem('campaign_product');
      const requirementsStr = localStorage.getItem('campaign_requirements');
      
      if (uuid) setCampaignUuid(uuid);
      if (name) setCampaignName(name);
      if (brand) {
        setBrandName(brand);
        setCampaignBrand(brand);
      }
      if (product) setExactProduct(product);
      
      if (requirementsStr) {
        try {
          const requirements = JSON.parse(requirementsStr);
          // Convert requirements to DOS and DON'Ts format
          const dos: string[] = [];
          const donts: string[] = [];
          
          // Iterate through categorized requirements
          for (const category in requirements) {
            if (requirements[category] && Array.isArray(requirements[category])) {
              requirements[category].forEach((req: string) => {
                // Assume all are DOS for now (brand can categorize in their UI)
                dos.push(req);
              });
            }
          }
          
          setDosText(dos.join('\n'));
          setDontsText(donts.join('\n'));
        } catch (err) {
          console.error('Failed to parse campaign requirements:', err);
        }
      }
      
      // Load creator identification from Lobby
      const storedCreatorFirstName = localStorage.getItem('creator_first_name');
      const storedCreatorSurname = localStorage.getItem('creator_surname');
      const storedCreatorName = localStorage.getItem('creator_name'); // For backward compatibility
      const storedCreatorEmail = localStorage.getItem('creator_email');
      if (storedCreatorName) setCreatorName(storedCreatorName);
      if (storedCreatorEmail) setCreatorEmail(storedCreatorEmail);
    }
  }, [location.search]);

  // Fetch projects on component mount
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const projectsData = await projectAPI.list();
        setProjects(projectsData);
      } catch (error) {
        console.error("Error fetching projects:", error);
      }
    };
    fetchProjects();
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      // Don't close if clicking inside language dropdown
      if (target.closest('[data-dropdown="language"]')) {
        return;
      }
      
      // Don't close if clicking inside project dropdown
      if (target.closest('[data-dropdown="project"]')) {
        return;
      }
      
      setLanguageDropdownOpen(false);
      setProjectDropdownOpen(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Language options must be defined before use
  const languageOptions: { value: string; label: string; icon: string }[] = [
    { value: "de", label: "German", icon: "https://GlobalArtInc.github.io/round-flags/flags/de.svg" },
    { value: "fr", label: "French", icon: "https://GlobalArtInc.github.io/round-flags/flags/fr.svg" },
    { value: "it", label: "Italian", icon: "https://GlobalArtInc.github.io/round-flags/flags/it.svg" },
    { value: "es", label: "Spanish", icon: "https://GlobalArtInc.github.io/round-flags/flags/es.svg" },
    { value: "pt", label: "Portuguese", icon: "https://GlobalArtInc.github.io/round-flags/flags/pt.svg" },
    { value: "en", label: "English", icon: "https://GlobalArtInc.github.io/round-flags/flags/gb.svg" },
  ];

  // Language-specific placeholder content for DOs and DON'Ts
  const getPlaceholderContent = (type: 'dos' | 'donts', language: string) => {
    const placeholders = {
      dos: {
        de: "z.B. \nErwähnen Sie wichtige Produktvorteile (z.B. vegan, frei von bestimmten Inhaltsstoffen, gut verträglich).\nTeilen Sie Ihre persönliche Erfahrung im Zusammenhang mit dem Anwendungsfall des Produkts, um eine authentische Verbindung zu schaffen.\nZeigen Sie deutlich die Produktverpackung, damit sie in Ihrem Content leicht erkennbar ist.\nFalls zutreffend, heben Sie verfügbare Optionen wie Geschmacksrichtungen, Varianten oder Formate hervor.",
        fr: "p.ex., \nMentionnez les avantages clés du produit (ex: vegan, sans certains ingrédients, facile à tolérer).\nPartagez votre expérience personnelle liée au cas d'usage du produit pour créer une connexion authentique.\nMontrez clairement l'emballage du produit pour qu'il soit facilement reconnaissable dans votre contenu.\nSi applicable, mettez en évidence les options disponibles comme les saveurs, variantes ou formats.",
        it: "ad es., \nMenziona i benefici chiave del prodotto (es. vegano, privo di determinati ingredienti, facile da tollerare).\nCondividi la tua esperienza personale relativa al caso d'uso del prodotto per creare una connessione autentica.\nMostra chiaramente il packaging del prodotto in modo che sia facilmente riconoscibile nel tuo contenuto.\nSe applicabile, evidenzia le opzioni disponibili come gusti, varianti o formati.",
        es: "p.ej.,\nMenciona los beneficios clave del producto (ej. vegano, libre de ciertos ingredientes, fácil de tolerar).\nComparte tu experiencia personal relacionada con el caso de uso del producto para crear una conexión auténtica.\nMuestra claramente el empaque del producto para que sea fácilmente reconocible en tu contenido.\nSi aplica, destaca las opciones disponibles como sabores, variantes o formatos.",
        pt: "P.ex., \nMencione os benefícios-chave do produto (ex: vegano, livre de certos ingredientes, fácil de tolerar).\nCompartilhe sua experiência pessoal relacionada ao caso de uso do produto para criar uma conexão autêntica.\nMostre claramente a embalagem do produto para que seja facilmente reconhecível em seu conteúdo.\nSe aplicável, destaque as opções disponíveis como sabores, variantes ou formatos.",
        en: "E.g., \nMention key product benefits (e.g., vegan, free from certain ingredients, easy to tolerate).\nShare your personal experience related to the product's use case to create an authentic connection.\nClearly show the product packaging so it's easily recognizable in your content.\nIf applicable, highlight available options like flavors, variants, or formats."
      },
      donts: {
        de: "z.B. \nVermeiden Sie das Tragen oder prominente Darstellen von Markenkleidung oder Produkten anderer Unternehmen.\nMachen Sie keine gesundheitsbezogenen Angaben, Heilversprechen oder garantierten Wirkungen im Zusammenhang mit dem Produkt.\nVermeiden Sie sexuelle, politische oder diskriminierende Aussagen sowie Verweise auf Rauchen, Drogen oder anstößige Sprache.\nErwähnen oder vergleichen Sie keine Konkurrenzmarken in Ihrem Content.",
        fr: "p.ex., \nÉvitez de porter ou de présenter de manière proéminente des vêtements de marque ou des produits d'autres entreprises.\nNe faites aucune allégation de santé, promesse de guérison ou effet garanti lié au produit.\nN'incluez aucune déclaration sexuelle, politique ou discriminatoire, ni de références au tabagisme, aux drogues ou au langage offensant.\nNe mentionnez ni ne comparez les marques concurrentes dans votre contenu.",
        it: "ad es., \nEvita di indossare o presentare in modo prominente abbigliamento o prodotti di marca di altre aziende.\nNon fare affermazioni sulla salute, promesse di guarigione o effetti garantiti relativi al prodotto.\nNon includere dichiarazioni sessuali, politiche o discriminatorie, né riferimenti a fumo, droghe o linguaggio offensivo.\nNon menzionare o confrontare marchi concorrenti nel tuo contenuto.",
        es: "p.ej., \nEvita usar o presentar de manera prominente ropa o productos de marca de otras empresas.\nNo hagas declaraciones de salud, promesas de curación o efectos garantizados relacionados con el producto.\nNo incluyas declaraciones sexuales, políticas o discriminatorias, ni referencias al tabaquismo, drogas o lenguaje ofensivo.\nNo menciones ni compares marcas competidoras en tu contenido.",
        pt: "P.ex., \nEvite usar ou apresentar de forma proeminente roupas ou produtos de marca de outras empresas.\nNão faça alegações de saúde, promessas de cura ou efeitos garantidos relacionados ao produto.\nNão inclua declarações sexuais, políticas ou discriminatórias, nem referências ao tabagismo, drogas ou linguagem ofensiva.\nNão mencione ou compare marcas concorrentes em seu conteúdo.",
        en: "E.g., \nAvoid wearing or featuring prominently branded clothing or products from other companies.\nDo not make or imply any health claims, cures, or guaranteed effects related to the product.\nDo not include any sexual, political, or discriminatory statements, as well as references to smoking, drugs, or offensive language.\nDo not mention or compare to competitor brands in your content."
      }
    };
    
    return placeholders[type][language as keyof typeof placeholders[typeof type]] || placeholders[type].en;
  };

  const handleStartNewAnalysis = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Reset all state
    setFile(null);
    setVideoUrl(null);
    setCampaignName("");
    setBrandName("");
    setExactProduct("");
    setDosText("");
    setDontsText("");
    setTranscriptLanguage("de");
    setStep(0);
    setAnalysisResult(null);
    setProgress(0);
    setIsAnalyzing(false);
    setIsDragging(false);
    // Navigate to the upload page to clear any URL params as a safeguard
    navigate(`${getBasePath()}/upload`);
  };

  const dialogOpenRef = useRef(false);

  useEffect(() => {
    dialogOpenRef.current = false;
    return () => { dialogOpenRef.current = false; };
  }, []);

  // Reset all state when landing on the main page ('/') without params
  useEffect(() => {
    if (location.pathname === "/" && !location.search) {
      setFile(null);
      setVideoUrl(null);
      setCampaignName("");
      setBrandName("");
      setExactProduct("");
      setDosText("");
      setDontsText("");
      setTranscriptLanguage("de");
      setStep(0);
      setAnalysisResult(null);
      setProgress(0);
      setIsAnalyzing(false);
      setIsDragging(false);
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("brandName")) setBrandName(params.get("brandName") || "");
    if (params.get("exactProduct")) setExactProduct(params.get("exactProduct") || "");
    if (params.get("transcriptLanguage")) setTranscriptLanguage(params.get("transcriptLanguage") || "de");
    if (params.get("campaignName")) setCampaignName(params.get("campaignName") || "");

    // Update DOs and DON'Ts from URL params
    const dosParam = params.get("dos");
    if (dosParam) {
      setDosText(dosParam.split('\\n').join('\n'));
    }
    const dontsParam = params.get("donts");
    if (dontsParam) {
      setDontsText(dontsParam.split('\\n').join('\n'));
    }

    // Set step from URL params
    const stepParam = params.get("step");
    if (stepParam) {
      const stepNumber = parseInt(stepParam, 10);
      if (!isNaN(stepNumber) && stepNumber >= 0 && stepNumber <= 5) {
        setStep(stepNumber);
      }
    }

    // Set project from URL params
    const projectIdParam = params.get("project_id");
    if (projectIdParam) {
      const projectId = parseInt(projectIdParam, 10);
      if (!isNaN(projectId)) {
        setSelectedProjectId(projectId);
        // When coming from a project folder, start at step 0 (video upload)
        if (!params.get("step")) {
          setStep(0);
        }
      }
    }

    // Load video from analysis if unique_id is provided
    const uniqueId = params.get("unique_id");
    if (uniqueId && !file) {
      loadVideoFromAnalysis(uniqueId);
    }
  }, [location.search]);

  // Function to load video from analysis
  const loadVideoFromAnalysis = async (uniqueId: string) => {
    try {
      const token = localStorage.getItem("token");
      let videoUrl = `${API_ROOT}/video/${uniqueId}?token=${token}`;
      let usePresignedUrl = false;

      // Try to get presigned URL for better performance
      try {
        const presignedResponse = await fetch(`${API_ROOT}/video/${uniqueId}/url?token=${token}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (presignedResponse.ok) {
          const data = await presignedResponse.json();
          if (data.presigned_url) {
            videoUrl = data.presigned_url;
            usePresignedUrl = true;
            console.log("✅ Using presigned URL for video loading");
          }
        }
      } catch (presignedError) {
        console.log("🔄 Presigned URL failed, using proxied access:", presignedError);
      }

      // Create a fake file object to represent the video
      console.log(`📥 Loading video from ${usePresignedUrl ? 'S3 direct' : 'backend proxy'}`);
      const response = await fetch(videoUrl, {
        // Add CORS headers for presigned URLs
        ...(usePresignedUrl ? {} : {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch video: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      const videoFile = new File([blob], `video_${uniqueId}.mp4`, { type: 'video/mp4' });

      setFile(videoFile);
      setVideoUrl(videoUrl);
      console.log(`✅ Video loaded successfully (${blob.size} bytes)`);
    } catch (error) {
      console.error("Error loading video from analysis:", error);
    }
  };

  // DRAG-AND-DROP + CLICK HANDLERS
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFile = e.dataTransfer.files[0];
      // Check if it's a video file
      if (droppedFile.type.startsWith("video/")) {
        setFile(droppedFile);
        setVideoUrl(URL.createObjectURL(droppedFile));
        setProgress(0);
        setAnalysisResult(null);
      } else {
        alert("Please drop a video file");
      }
    }
  };

  const handleClick = () => {
    if (dialogOpenRef.current) return;
    dialogOpenRef.current = true;
    inputRef.current?.click();
  };

  // FILE CHANGE HANDLER (onChange in <input>)
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dialogOpenRef.current = false;
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setVideoUrl(URL.createObjectURL(selectedFile));
    }
  };

  // Only reset file input and state when user clicks 'Change'
  const handleChangeVideo = () => {
    setFile(null);
    setVideoUrl(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  // SUBMIT HANDLER
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!file) return;

    // Campaign mode: skip to analysis directly
    if (isCampaignMode) {
      // Use the pre-filled campaign data
      await analyzeVideo();
      return;
    }

    // Stay on current step but start analyzing
    // Use flushSync to FORCE React to render immediately before continuing
    flushSync(() => {
      setIsAnalyzing(true);
      setProgress(0); // Start at 0% for realistic progress
      setAnalysisResult(null);
    });

    // Small delay to ensure browser paints the progress bar
    await new Promise(resolve => setTimeout(resolve, 100));

    await analyzeVideo();
  };

  // Extracted analysis logic
  const analyzeVideo = async () => {
    if (!file) return; // Add safety check
    
    // Use flushSync to FORCE React to render immediately before continuing
    flushSync(() => {
      setIsAnalyzing(true);
      setProgress(0); // Start at 0% for realistic progress
      setAnalysisResult(null);
    });

    // Small delay to ensure browser paints the progress bar
    await new Promise(resolve => setTimeout(resolve, 100));

    try {
      // Build FormData
      const formData = new FormData();
      formData.append("file", file);
      formData.append("session_id", crypto.randomUUID());
      
      // Campaign mode: send campaign_uuid and creator info
      if (isCampaignMode && campaignUuid) {
        formData.append("campaign_uuid", campaignUuid);
        // Add creator identification - read from localStorage
        const storedCreatorFirstName = localStorage.getItem('creator_first_name');
        const storedCreatorSurname = localStorage.getItem('creator_surname');
        const storedCreatorName = localStorage.getItem('creator_name'); // For backward compatibility
        
        formData.append("creator_email", creatorEmail);
        if (storedCreatorFirstName) {
          formData.append("creator_first_name", storedCreatorFirstName);
        }
        if (storedCreatorSurname) {
          formData.append("creator_surname", storedCreatorSurname);
        }
        // Keep creator_name for backward compatibility
        if (storedCreatorName) {
          formData.append("creator_name", storedCreatorName);
        }
        
        // Add version number for revision tracking
        const storedVersion = localStorage.getItem('submission_version');
        if (storedVersion) {
          formData.append("version", storedVersion);
          console.log(`📊 Submitting video version: V${storedVersion}`);
        }
      } else {
        // Creator mode: send all briefing data
        formData.append("brand_name", brandName);
        formData.append("exact_product", exactProduct);
        
        const dosRequirements = dosText.split(/[;.\n]/).map(s => s.trim()).filter(Boolean);
        const dontsRequirements = dontsText.split(/[;.\n]/).map(s => s.trim()).filter(Boolean);

        dosRequirements.forEach(req => formData.append("dos", req));
        dontsRequirements.forEach(req => formData.append("donts", req));

        formData.append("transcript_language", transcriptLanguage);
        formData.append("campaign_name", campaignName);
        
        // Add project_id if selected
        if (selectedProjectId) {
          formData.append("project_id", selectedProjectId.toString());
        }

        // Build input_data JSON for creator mode
        const inputData = {
          brand_name: brandName,
          product_name: exactProduct,
          hooks: dosRequirements,
          elements: dontsRequirements,
          campaign_name: campaignName,
          transcript_language: transcriptLanguage,
        };
        formData.append("input_data", JSON.stringify(inputData));
      }

      // Send the analysis request with file and data
      
      // Use different endpoints for campaign vs creator mode
      const endpoint = isCampaignMode ? "/analyze-campaign-video" : "/analyze-video";
      const response = await fetch(`${API_ROOT}${endpoint}`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${localStorage.getItem("token")}`
        },
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Analysis failed: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      
      // Ensure progress shows 100% before navigating
      setProgress(100);
      setAnalysisResult(result);

      // Set flag to refresh sidebar when user returns to dashboard
      localStorage.setItem("refreshSidebar", "true");
      localStorage.setItem("refreshSidebarTimestamp", Date.now().toString());

      // Small delay to show 100% progress before navigating
      await new Promise(resolve => setTimeout(resolve, 500));

      // Navigate directly to full results page (Step 3)
      // For campaign mode (public links): use /campaign-result/ (public route, no auth required)
      // For authenticated users: use /result/ (protected route)
      if (result?.unique_id) {
        if (isCampaignMode) {
          // Public campaign result page - works for both unauthenticated creators and brands
          navigate(`/campaign-result/${result.unique_id}?campaign=${campaignUuid}`);
        } else {
          // Authenticated creator flow
          navigate(`${getBasePath()}/result/${result.unique_id}`);
        }
      } else {
        // Fallback: move to share step
        setStep(4);
      }
    } catch (error) {
      setAnalysisResult(null);
      // Stay on current step (DOs & DON'Ts) on error
      // Show user-friendly error message
      alert(`Error: ${error instanceof Error ? error.message : 'Upload failed. Please try again.'}`);
    } finally {
      setIsAnalyzing(false);
      setProgress(0);
    }
  };



  // Dynamic keys if needed
  const brandKeyIdent = `custom_brand_identification ${brandName.trim()}`;
  const brandKeyProp = `custom_brand_proportion ${brandName.trim()}`;
  const brandKeyAppearance = `custom_brand_first_appearance ${brandName.trim()}`;

  // The fields we want to display, including dynamic brand keys
  const displayedFields: Record<string, any> = {
    [brandKeyIdent]: (analysisResult as any)?.[brandKeyIdent],
    [brandKeyProp]: (analysisResult as any)?.[brandKeyProp],
    [brandKeyAppearance]: (analysisResult as any)?.[brandKeyAppearance],

    custom_product_detection: (analysisResult as any)?.custom_product_detection,
    custom_product_proportion: (analysisResult as any)?.custom_product_proportion,
    custom_product_first_appearance: (analysisResult as any)?.custom_product_first_appearance,
    visible_brands: (analysisResult as any)?.visible_brands,
    visible_text: (analysisResult as any)?.visible_text,
    contains_sensitive_content: (analysisResult as any)?.contains_sensitive_content,
    transcription: (analysisResult as any)?.transcription,
    complies_with_dos: (analysisResult as any)?.complies_with_dos,
    complies_with_donts: (analysisResult as any)?.complies_with_donts,
    first_dos_timestamp: (analysisResult as any)?.first_dos_timestamp,
    first_donts_timestamp: (analysisResult as any)?.first_donts_timestamp,
    complies_with_dos_heard: (analysisResult as any)?.complies_with_dos_heard,
    complies_with_donts_heard: (analysisResult as any)?.complies_with_donts_heard,
    total_processing_time: (analysisResult as any)?.total_processing_time,
  };

  // User-friendly labels
  const fieldLabels: Record<string, string> = {
    custom_brand_identification: "Brand Identified",
    custom_brand_proportion: "Brand Proportion",
    custom_brand_first_appearance: "Brand First Appearance",
    custom_product_detection: "Product Detected",
    custom_product_proportion: "Product Proportion",
    custom_product_first_appearance: "Product First Appearance",
    visible_brands: "Visible Brands",
    contains_sensitive_content: "Contains Sensitive Content",
    total_processing_time: "Total Processing Time",
  };

  // ---------------------------------------
  // ORDERED SECTIONS & FIELDS
  // ---------------------------------------
  const orderedSections = [
    {
      title: "Brand Detection",
      fields: [
        "custom_brand_identification",
        "custom_brand_proportion",
        "custom_brand_first_appearance",
      ],
    },
    {
      title: "Product Detection",
      fields: [
        "custom_product_detection",
        "custom_product_proportion",
        "custom_product_first_appearance",
      ],
    },
    {
      title: "Content Checks",
      fields: [
        "contains_sensitive_content",
        "visible_brands",
      ],
    },
    {
      title: "Results",
      fields: ["results_placeholder"],
      content: "This is a placeholder for the Results step."
    },
    {
      title: "Share",
      fields: ["share_placeholder"],
      content: "This is a placeholder for the Share step."
    },
  ];

  const handleReuseSettings = () => {
    const params = new URLSearchParams();
    if (campaignName) params.set("campaignName", campaignName);
    if (brandName) params.set("brandName", brandName);
    if (exactProduct) params.set("exactProduct", exactProduct);
    if (transcriptLanguage) params.set("transcriptLanguage", transcriptLanguage);

    const dosRequirements = dosText.split(/[;,\n]/).map(s => s.trim()).filter(Boolean);
    const dontsRequirements = dontsText.split(/[;,\n]/).map(s => s.trim()).filter(Boolean);

    if (dosRequirements.length > 0) params.set("dos", dosRequirements.join("\\n"));
    if (dontsRequirements.length > 0) params.set("donts", dontsRequirements.join("\\n"));
    
    navigate(`/upload?${params.toString()}`);
  };

  // Stepper navigation
  const nextStep = () => {
    if (step < 4) setStep(step + 1);
  };

  const prevStep = () => {
    if (step > 0) setStep(step - 1);
  };

  const formRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fullscreen handlers
  const handleVideoFullscreenOpen = () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
    setIsVideoFullscreen(true);
  };
  const handleVideoFullscreenClose = () => {
    if (fullscreenVideoRef.current) {
      fullscreenVideoRef.current.pause();
    }
    setIsVideoFullscreen(false);
  };
  const handleModalVideoLoad = () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };
  const handleModalVideoPlay = () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };
  useEffect(() => {
    if (isVideoFullscreen && videoRef.current) {
      videoRef.current.pause();
    }
  }, [isVideoFullscreen]);

  // Timer-based progress for visual feedback during analysis (90 seconds)
  useEffect(() => {
    if (!isAnalyzing) {
      setTimerProgress(0);
      return;
    }

    const startTime = Date.now();
    const duration = 90000; // 90 seconds

    const intervalId = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const percentage = Math.min(100, Math.round((elapsed / duration) * 100));
      setTimerProgress(percentage);

      if (percentage >= 100) {
        clearInterval(intervalId);
      }
    }, 100); // Update every 100ms

    return () => clearInterval(intervalId);
  }, [isAnalyzing]);

  // Move startShepherdTour function inside component
  const startShepherdTour = () => {
    // Close sidebar at the beginning of the tour
    setSidebarOpen(false);
    
    const tour = new Shepherd.Tour({
      defaultStepOptions: {
        cancelIcon: { enabled: true },
        scrollTo: true,
        classes: 'shadow-lg bg-white shepherd-custom-buttons',
      },
      useModalOverlay: true,
    });
    
    tourSteps.forEach(step => tour.addStep(step));
    tour.start();
  };

  // Campaign mode tour - simpler tour for creators uploading via campaign link
  const startCampaignTour = () => {
    const tour = createCreatorUploadTour();
    tour.start();
  };

  // -------------------------------------------------
  // Campaign mode layout (matches campaign-loader.tsx)
  // -------------------------------------------------
  if (isCampaignMode) {
    return (
      <div
        className="min-h-screen flex flex-col relative"
        style={{
          background: `linear-gradient(135deg, ${PALETTE.brightSnow} 0%, ${PALETTE.americanSilver}30 100%)`
        }}
      >
        {/* Feature Request & Help/Tour Buttons - Top Right */}
        <div className="absolute top-4 right-8 z-10 flex items-center gap-3">
          <FeatureRequestButton />
          <button
            onClick={startCampaignTour}
            aria-label="Help / Tour"
            className="tour-help-btn bg-white border border-gray-300 shadow-md rounded-full w-10 h-10 flex items-center justify-center hover:bg-gray-100 transition"
            style={{ boxShadow: '0 2px 8px rgba(36,56,55,0.10)' }}
          >
            <HelpCircle size={20} color={PALETTE.gunmetal} />
          </button>
        </div>

        <div className="flex-1 flex flex-col overflow-hidden">
          <main className="flex-1 overflow-hidden">
            <div className="max-w-4xl mx-auto px-6 py-12">
              {/* Process Wizard - Step 2: Video Upload */}
              <div className="flex items-center justify-center gap-4 mb-12 w-full max-w-4xl">
                {['Contact Information', 'Video Upload', 'Success Scan'].map((label, idx) => {
                  const isActive = idx === 1; // Step 2 (Video Upload) is active
                  const isCompleted = idx < 1; // Step 1 is completed
                  return (
                    <React.Fragment key={label}>
                      <div className="flex items-center flex-1 justify-center">
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center font-bold flex-shrink-0"
                          style={{
                            background: isActive ? PALETTE.gunmetal : (isCompleted ? PALETTE.inchworm : PALETTE.americanSilver),
                            color: isActive ? PALETTE.brightSnow : PALETTE.gunmetal
                          }}
                        >
                          {idx + 1}
                        </div>
                        <span
                          className="ml-2 text-sm font-medium whitespace-nowrap hidden sm:inline"
                          style={{ color: isActive || isCompleted ? PALETTE.gunmetal : PALETTE.americanSilver }}
                        >
                          {label}
                        </span>
                      </div>
                      {idx < 2 && (
                        <div
                          className="h-0.5 mx-2 sm:mx-4 flex-1"
                          style={{
                            minWidth: 20,
                            background: isCompleted ? PALETTE.inchworm : PALETTE.americanSilver,
                          }}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>

              {/* Always mount the file input, but hide it visually */}
              <input
                type="file"
                accept="video/*"
                ref={inputRef}
                onChange={handleFileChange}
                onBlur={() => { dialogOpenRef.current = false; }}
                onFocus={() => { dialogOpenRef.current = false; }}
                className="hidden"
              />

              {/* Video or Drag & Drop */}
              <div className="flex justify-center">
                {videoUrl ? (
                  <div className="space-y-6">
                <div className="flex justify-center">
                  <div className={`relative w-full aspect-[9/16] max-w-xs ${isVideoFullscreen ? 'video-disabled' : ''}`}>
                    <video
                      key={videoUrl}
                      ref={videoRef}
                      src={videoUrl}
                      controls={!isVideoFullscreen}
                      className="w-full h-full rounded-lg cursor-pointer"
                      style={{ objectFit: 'cover' }}
                      playsInline
                      preload="metadata"
                      crossOrigin="anonymous"
                      lang="en"
                      onClick={handleVideoFullscreenOpen}
                      muted={isVideoFullscreen}
                    >
                      Your browser does not support the video tag.
                    </video>
                    <Button
                      type="button"
                      onClick={handleChangeVideo}
                      variant="outline"
                      className="absolute top-2 right-2 text-xs px-2 py-1 rounded-lg change-video-button"
                      style={{ background: 'rgba(255,255,255,0.9)', borderColor: PALETTE.paleViolet, color: PALETTE.paleViolet, zIndex: 10 }}
                    >
                      Change Video
                    </Button>

                    {/* Spinning circle overlay during analysis */}
                    {isAnalyzing && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/70 backdrop-blur-sm rounded-lg z-20">
                        <div className="relative">
                          <div 
                            className="animate-spin rounded-full h-16 w-16"
                            style={{ 
                              border: `4px solid ${PALETTE.americanSilver}`,
                              borderTopColor: PALETTE.inchworm
                            }}
                          ></div>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-sm font-semibold" style={{ color: PALETTE.gunmetal }}>
                              {timerProgress}%
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                  </div>
                ) : (
                  <Card className="border-0 shadow-lg" style={{ width: '384px' }}>
                    <CardContent className="p-8">
                      <div
                        className="video-dropzone border-2 border-dashed rounded-lg flex flex-col items-center justify-center text-center cursor-pointer transition-all hover:bg-gray-50/50"
                        style={{
                          width: '320px',
                          height: '568.88px',
                          borderColor: `${PALETTE.americanSilver}80`,
                          background: '#F9F9F9'
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.currentTarget.style.borderColor = PALETTE.inchworm;
                          e.currentTarget.style.background = `${PALETTE.inchworm}10`;
                        }}
                        onDragLeave={(e) => {
                          e.currentTarget.style.borderColor = `${PALETTE.americanSilver}80`;
                          e.currentTarget.style.background = '#F9F9F9';
                        }}
                        onDrop={handleDrop}
                        onClick={handleClick}
                      >
                        <Upload className="w-12 h-12 mx-auto mb-4" style={{ color: `${PALETTE.gunmetal}40`, strokeWidth: 1.5 }} />
                        <p className="text-lg font-semibold mb-3" style={{ color: PALETTE.gunmetal }}>
                          Drag and drop your file here
                        </p>
                        <p className="text-base mb-4" style={{ color: `${PALETTE.gunmetal}60` }}>
                          or{' '}
                          <span className="font-medium hover:underline cursor-pointer" style={{ color: PALETTE.inchworm }}>
                            Browse Files
                          </span>
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </main>
        </div>

        {/* Campaign Mode - Back + Analyze Buttons */}
        {file && (
          <div className="fixed bottom-8 left-0 right-0 z-20 flex flex-col items-center gap-2">
            {!creatorEmail && (
              <p className="text-sm" style={{ color: PALETTE.orange }}>
                Please enter your email above before analyzing
              </p>
            )}
            <div className="flex items-center justify-center gap-4">
              <Button
                type="button"
                onClick={() => {
                  if (campaignUuid) {
                    navigate(`/upload/${campaignUuid}`);
                  } else {
                    navigate('/upload');
                  }
                }}
                disabled={isAnalyzing}
                className="rounded-full px-6 h-12 flex items-center justify-center shadow-lg text-base font-semibold transition-all duration-300 gap-2"
                style={{
                  background: PALETTE.brightSnow,
                  border: `1px solid ${PALETTE.americanSilver}`,
                  cursor: isAnalyzing ? 'not-allowed' : 'pointer',
                  color: PALETTE.gunmetal,
                  borderRadius: '9999px'
                }}
                aria-label="Back to Contact Information"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Back</span>
              </Button>

              <Button
                type="button"
                onClick={() => analyzeVideo()}
                disabled={isAnalyzing || !creatorEmail}
                className="rounded-full px-8 h-12 flex items-center justify-center shadow-lg text-base font-bold transition-all duration-300 gap-2"
                style={{
                  background: (isAnalyzing || !creatorEmail) ? PALETTE.americanSilver : PALETTE.inchworm,
                  border: 'none',
                  cursor: (isAnalyzing || !creatorEmail) ? 'not-allowed' : 'pointer',
                  color: PALETTE.gunmetal,
                  borderRadius: '9999px'
                }}
                aria-label="Analyze Video"
              >
                <span>{isAnalyzing ? 'Analyzing...' : 'Start Video Check'}</span>                
              </Button>
            </div>
          </div>
        )}

        {/* Powered By - Bottom Right */}
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-4">
          <span className="text-sm" style={{ color: `${PALETTE.gunmetal}60` }}>
            powered by{' '}
            <a 
              href="https://brandcomply.ai" 
              target="_blank" 
              rel="noopener noreferrer"
              className="font-semibold hover:underline"
              style={{ color: PALETTE.gunmetal }}
            >
              BrandComply
            </a>
          </span>
        </div>

        {/* Tour styles */}
        <style>{tourStyles}</style>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden">
      <SEO
        title="Upload Video"
        description="Upload your video to BrandComply for AI-powered compliance analysis against your campaign brief."
        path="/video-upload"
      />
      {/* Sidebar - Hide in campaign mode */}
      {!isCampaignMode && (
        <>
          {sidebarOpen ? (
            <AnalysisSidebar onHideSidebar={() => setSidebarOpen(false)} />
          ) : (
            <FoldedSidebar onShowSidebar={() => setSidebarOpen(true)} onNewAnalysisClick={handleStartNewAnalysis} />
          )}
        </>
      )}

      {/* Main Content */}
      <div
        className="flex-1 flex flex-col overflow-hidden relative"
        style={{ 
          background: `linear-gradient(135deg, ${PALETTE.brightSnow} 0%, ${PALETTE.americanSilver}30 100%)`
        }}
      >
        {/* Feature Request & Help/Tour Buttons - Top Right */}
        <div className="absolute top-4 right-8 z-10 flex items-center gap-3">
          <FeatureRequestButton />
          <button
            onClick={isCampaignMode ? startCampaignTour : startShepherdTour}
            aria-label="Help / Tour"
            className="tour-help-btn shepherd-help-btn bg-white border border-gray-300 shadow-md rounded-full w-10 h-10 flex items-center justify-center hover:bg-gray-100 transition"
            style={{ boxShadow: '0 2px 8px rgba(36,56,55,0.10)' }}
          >
            <HelpCircle size={20} color="#243837" />
          </button>
        </div>
        
        {/* Show sidebar button when collapsed */}
        
      {/* Improved Stepper with better horizontal spacing */}
      {!isCampaignMode && (
        <div className="flex items-center justify-center gap-6 mb-4 w-full max-w-4xl">
          {STEPS.map((label, idx) => {
            // Determine if step is clickable
            const isClickable = idx <= step || (idx === 1 && file) || (idx === 2 && file && brandName && exactProduct);
            
            return (
              <div key={label} className="flex items-center flex-1 justify-center">
                <div
                  className={`flex items-center justify-center rounded-full text-sm font-bold flex-shrink-0 ${
                    isClickable ? 'cursor-pointer hover:opacity-80' : ''
                  }`}
                  style={{
                    width: 32,
                    height: 32,
                    background: step === idx ? PALETTE.gunmetal : step > idx ? PALETTE.inchworm : PALETTE.americanSilver,
                    color: step === idx ? PALETTE.brightSnow : PALETTE.gunmetal,
                  }}
                  onClick={() => {
                    if (isClickable) {
                      setStep(idx);
                    }
                  }}
                  title={isClickable ? `Go to ${label}` : `Complete previous steps to access ${label}`}
                >
                  {idx + 1}
                </div>
                <span
                  className={`ml-2 text-sm font-medium whitespace-nowrap ${
                    isClickable ? 'cursor-pointer hover:opacity-80' : ''
                  }`}
                  style={{ color: step === idx ? PALETTE.gunmetal : PALETTE.americanSilver }}
                  onClick={() => {
                    if (isClickable) {
                      setStep(idx);
                    }
                  }}
                  title={isClickable ? `Go to ${label}` : `Complete previous steps to access ${label}`}
                >
                  {label}
                </span>
                {idx < STEPS.length - 1 && (
                  <div
                    className="h-0.5 mx-4 flex-1"
                    style={{
                      minWidth: 40,
                      background: step > idx ? PALETTE.inchworm : PALETTE.americanSilver,
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Campaign Mode Stepper - 3 Steps */}
      {isCampaignMode && (
        <div className="flex-1 flex flex-col overflow-hidden py-4">
          {/* Process Wizard - Step 2: Video Upload */}
          <div className="flex items-center justify-center gap-4 px-6 mb-4 flex-shrink-0">
            {['Contact Information', 'Video Upload', 'Review Results'].map((label, idx) => {
              const isActive = idx === 1; // Step 2 (Video Upload) is active
              const isCompleted = idx < 1; // Step 1 is completed
              return (
                <React.Fragment key={label}>
                  <div className="flex items-center flex-1 justify-center">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center font-bold flex-shrink-0"
                      style={{
                        background: isActive ? PALETTE.gunmetal : (isCompleted ? PALETTE.inchworm : PALETTE.americanSilver),
                        color: isActive ? PALETTE.brightSnow : PALETTE.gunmetal
                      }}
                    >
                      {idx + 1}
                    </div>
                    <span 
                      className="ml-2 text-sm font-medium whitespace-nowrap hidden sm:inline"
                      style={{ color: isActive || isCompleted ? PALETTE.gunmetal : PALETTE.americanSilver }}
                    >
                      {label}
                    </span>
                  </div>
                  {idx < 2 && (
                    <div
                      className="h-0.5 mx-2 sm:mx-4 flex-1"
                      style={{
                        minWidth: 20,
                        background: isCompleted ? PALETTE.inchworm : PALETTE.americanSilver,
                      }}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Always mount the file input, but hide it visually */}
          <input
            type="file"
            accept="video/*"
            ref={inputRef}
            onChange={handleFileChange}
            onBlur={() => { dialogOpenRef.current = false; }}
            onFocus={() => { dialogOpenRef.current = false; }}
            className="hidden"
          />

          {/* In campaign mode, always show step 0 (video upload) */}
          {(step === 0 || isCampaignMode) && (
            <div className="flex-1 flex justify-center items-center px-6 overflow-hidden">
            {videoUrl ? (
              /* Video Preview */
              <div className="space-y-6">
                <div className="flex justify-center">
                  <div className={`relative w-full aspect-[9/16] max-w-xs ${isVideoFullscreen ? 'video-disabled' : ''}`}> 
                    <video 
                      key={videoUrl}
                      ref={videoRef}
                      src={videoUrl}
                      controls={!isVideoFullscreen}
                      className="w-full h-full rounded-lg cursor-pointer" 
                      style={{ objectFit: 'cover' }}
                      playsInline
                      preload="metadata"
                      crossOrigin="anonymous"
                      lang="en"
                      onClick={handleVideoFullscreenOpen}
                      muted={isVideoFullscreen}
                    >
                      Your browser does not support the video tag.
                    </video>
                    <Button
                      type="button"
                      onClick={handleChangeVideo}
                      variant="outline"
                      className="absolute top-2 right-2 text-xs px-2 py-1 rounded-lg change-video-button"
                      style={{ background: 'rgba(255,255,255,0.9)', borderColor: PALETTE.paleViolet, color: PALETTE.paleViolet, zIndex: 10 }}
                    >
                      Change Video
                    </Button>

                    {/* Spinning circle overlay during analysis */}
                    {isCampaignMode && isAnalyzing && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/70 backdrop-blur-sm rounded-lg z-20">
                        <div className="relative">
                          <div 
                            className="animate-spin rounded-full h-16 w-16"
                            style={{ 
                              border: `4px solid ${PALETTE.americanSilver}`,
                              borderTopColor: PALETTE.inchworm
                            }}
                          ></div>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-sm font-semibold" style={{ color: PALETTE.gunmetal }}>
                              {timerProgress}%
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              /* Drag and Drop Zone - Same size and aspect ratio as video player (320x568.88), styled like briefing upload */
              <Card className="border-0 shadow-lg" style={{ width: '384px' }}>
                <CardContent className="p-8">
                  <div
                    className="border-2 border-dashed rounded-lg flex flex-col items-center justify-center text-center cursor-pointer transition-all hover:bg-gray-50/50"
                    style={{ 
                      width: '320px',
                      height: '568.88px',
                      borderColor: `${PALETTE.americanSilver}80`, 
                      background: '#F9F9F9' 
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.currentTarget.style.borderColor = PALETTE.inchworm;
                      e.currentTarget.style.background = `${PALETTE.inchworm}10`;
                    }}
                    onDragLeave={(e) => {
                      e.currentTarget.style.borderColor = `${PALETTE.americanSilver}80`;
                      e.currentTarget.style.background = '#F9F9F9';
                    }}
                    onDrop={handleDrop}
                    onClick={handleClick}
                  >
                    <Upload className="w-12 h-12 mx-auto mb-4" style={{ color: `${PALETTE.gunmetal}40`, strokeWidth: 1.5 }} />
                    <p className="text-lg font-semibold mb-3" style={{ color: PALETTE.gunmetal }}>
                      Drag and drop your file here
                    </p>
                    <p className="text-base mb-4" style={{ color: `${PALETTE.gunmetal}60` }}>
                      or{' '}
                      <span className="font-medium hover:underline cursor-pointer" style={{ color: PALETTE.inchworm }}>
                        Browse Files
                      </span>
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
            </div>
          )}
        </div>
      )}

      <div className="w-full max-w-6xl flex-grow flex flex-col">
        {/* Always mount the file input, but hide it visually */}
        <input
          type="file"
          accept="video/*"
          ref={inputRef}
          onChange={handleFileChange}
          onBlur={() => { dialogOpenRef.current = false; }}
          onFocus={() => { dialogOpenRef.current = false; }}
          className="hidden"
        />

        <div className="flex-grow flex flex-col justify-start pt-4 sm:pt-6 lg:pt-8 xl:pt-6"> {/* Responsive top padding */}
          {/* Step 1: Brand & Product - Hidden in campaign mode */}
          {step === 1 && !isCampaignMode && (
            <div className="w-full mx-auto px-4 sm:px-6">
              <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-start justify-center max-w-7xl mx-auto"> {/* Professional spacing */}
                {/* Left side - Video (Compact) */}
                <div className="w-full max-w-xs lg:max-w-[280px] xl:max-w-[320px] mx-auto lg:mx-0 flex-shrink-0"> {/* Much smaller, professional video size */}
                  {videoUrl && (
                    <Card className="p-3 bg-white rounded-lg shadow-lg border border-gray-200">
                      <div className={`relative w-full ${isVideoFullscreen ? 'video-disabled' : ''}`}> 
                        <video 
                          key={videoUrl}
                          ref={videoRef}
                          src={videoUrl}
                          controls={!isVideoFullscreen}
                          className="w-full rounded-lg cursor-pointer aspect-[9/16]" 
                          style={{ objectFit: 'cover' }}
                          playsInline
                          preload="metadata"
                          lang="en"
                          onClick={handleVideoFullscreenOpen}
                          muted={isVideoFullscreen}
                        >
                          Your browser does not support the video tag.
                        </video>
                        <Button
                          type="button"
                          onClick={handleChangeVideo}
                          variant="outline"
                          className="absolute top-2 right-2 text-xs px-2 py-1 rounded-lg change-video-button"
                          style={{ background: 'rgba(255,255,255,0.7)', borderColor: PALETTE.paleViolet, color: PALETTE.paleViolet, zIndex: 1 }}
                        >
                          Change
                        </Button>
                      </div>
                    </Card>
                  )}
                </div>

                                  {/* Right side - Form Fields (Primary Content) */}
                <div className="w-full flex-1 max-w-2xl lg:max-w-none space-y-6"> {/* Spacious form area - content takes priority */}
                  {/* Campaign Name */}
                  <div>
                    <label className="text-lg font-semibold block mb-3" style={{ color: PALETTE.gunmetal }}>
                      Campaign Name {isCampaignMode && <span className="text-sm font-normal" style={{ color: `${PALETTE.gunmetal}60` }}>(Locked)</span>}
                    </label>
                    <Input 
                      type="text" 
                      placeholder="Enter campaign name" 
                      value={campaignName} 
                      onChange={e => setCampaignName(e.target.value)} 
                      className="text-lg py-3 px-4 h-auto" 
                      disabled={isCampaignMode}
                    />
                  </div>

                  {/* Video Language */}
                  <div>
                    <label className="text-lg font-semibold block mb-3" style={{ color: PALETTE.gunmetal }}>
                      Video Language
                    </label>
                    <div className="relative" data-dropdown="language">
                      <button
                        type="button"
                        className="w-full rounded-md border border-input bg-background px-4 py-3 text-lg text-left flex items-center justify-between h-auto"
                        onClick={(e) => {
                          e.stopPropagation();
                          setLanguageDropdownOpen(!languageDropdownOpen);
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <img
                            src={languageOptions.find(opt => opt.value === transcriptLanguage)?.icon}
                            alt=""
                            className="w-5 h-5 rounded-full"
                          />
                          <span>{languageOptions.find(opt => opt.value === transcriptLanguage)?.label}</span>
                        </div>
                        <ChevronDown className={`w-4 h-4 transition-transform ${languageDropdownOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {languageDropdownOpen && (
                        <div className="absolute top-full mt-1 w-full bg-white border rounded-md shadow-lg z-10">
                          {languageOptions.map((opt) => (
                            <div
                              key={opt.value}
                              className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-3"
                              onClick={(e) => {
                                e.stopPropagation();
                                setTranscriptLanguage(opt.value);
                                setLanguageDropdownOpen(false);
                              }}
                            >
                              <img src={opt.icon} alt={opt.label} className="w-5 h-5 rounded-full" />
                              <span>{opt.label}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Brand Name */}
                  <div>
                    <label className="text-lg font-semibold block mb-3" style={{ color: PALETTE.gunmetal }}>
                      Brand {isCampaignMode && <span className="text-sm font-normal" style={{ color: `${PALETTE.gunmetal}60` }}>(Locked)</span>}
                    </label>
                    <Input 
                      type="text" 
                      placeholder="Enter brand name" 
                      value={brandName} 
                      onChange={e => setBrandName(e.target.value)} 
                      className="text-lg py-3 px-4 h-auto" 
                      disabled={isCampaignMode}
                    />
                  </div>
                  {/* Product */}
                  <div>
                    <label className="text-lg font-semibold block mb-3" style={{ color: PALETTE.gunmetal }}>
                      Product {isCampaignMode && <span className="text-sm font-normal" style={{ color: `${PALETTE.gunmetal}60` }}>(Locked)</span>}
                    </label>
                    <Input 
                      type="text" 
                      placeholder="Enter product name" 
                      value={exactProduct} 
                      onChange={e => setExactProduct(e.target.value)} 
                      className="text-lg py-3 px-4 h-auto" 
                      disabled={isCampaignMode}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

                  {/* Step 2: DOs & DON'Ts - Hidden in campaign mode */}
          {step === 2 && !isCampaignMode && (
            <form onSubmit={handleSubmit} ref={formRef}>
              <div className="w-full max-w-7xl mx-auto px-4 sm:px-6">
                <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-start justify-center"> {/* Professional layout */}

                  {/* Left side - Video (Compact) */}
                  <div className="w-full max-w-xs lg:max-w-[280px] xl:max-w-[320px] mx-auto lg:mx-0 flex-shrink-0">
                    {videoUrl && (
                      <Card className="p-3 bg-white rounded-lg shadow-lg border border-gray-200 h-full">
                        <div className={`relative h-full ${isVideoFullscreen ? 'video-disabled' : ''}`}> 
                          <video 
                            key={videoUrl}
                            ref={videoRef}
                            src={videoUrl}
                            controls={!isVideoFullscreen}
                            className="w-full h-full rounded-lg cursor-pointer aspect-[9/16]" 
                            style={{ objectFit: 'cover' }}
                            playsInline
                          preload="metadata"
                            lang="en"
                            onClick={handleVideoFullscreenOpen}
                            muted={isVideoFullscreen}
                          >
                            Your browser does not support the video tag.
                          </video>
                        </div>
                      </Card>
                    )}
                  </div>

                  {/* Right side - DOs & DON'Ts (Primary Content) */}
                  <div className="w-full flex-1 max-w-4xl lg:max-w-none min-w-0"> {/* Much more spacious - content is priority */}

                    <div className="flex flex-col gap-3 h-[400px] sm:h-[450px] lg:h-[497px] xl:h-[568px]"> {/* Responsive height matching video aspect-[9/16] at different breakpoints */}
                      {/* DOs Section */}
                      <div className={`p-5 rounded-lg shadow-lg border border-gray-200 flex flex-col transition-all duration-300 ${isAnalyzing ? 'opacity-60' : ''}`} style={{ backgroundColor: '#f0fdf4', flex: '1' }}>
                        <div className="flex items-center gap-2 mb-3">
                          <Check className="w-5 h-5 text-green-600" />
                          <h3 className="font-bold text-xl" style={{ color: PALETTE.gunmetal }}>DOs {isCampaignMode && <span className="text-sm font-normal" style={{ color: `${PALETTE.gunmetal}60` }}>(Locked)</span>}</h3>
                        </div>
                        <div className="relative flex-grow">
                          <Textarea
                            placeholder={getPlaceholderContent('dos', transcriptLanguage)}
                            value={dosText}
                            onChange={(e) => setDosText(e.target.value)}
                            className="w-full h-full resize-none placeholder:text-gray-400 bg-transparent border-0 focus:ring-0 p-0 text-base leading-relaxed"
                            style={{ color: PALETTE.gunmetal }}
                            disabled={isAnalyzing || isCampaignMode}
                            onMouseEnter={(e) => {
                              const tooltip = e.currentTarget.nextElementSibling as HTMLElement;
                              if (tooltip) {
                                tooltip.style.opacity = '1';
                                setTimeout(() => {
                                  if (tooltip) tooltip.style.opacity = '0';
                                }, 2000);
                              }
                            }}
                          />
                          {/* Tooltip */}
                          <div className="absolute -top-16 left-1/2 transform -translate-x-1/2 opacity-0 transition-opacity duration-300 pointer-events-none z-20">
                            <div className="bg-white text-gray-800 text-xs rounded-lg py-2 px-3 text-center border border-gray-200 shadow-lg" style={{ minWidth: '300px' }}>
                              Separate multiple requirements with semicolons (;), periods (.), or new lines
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-white"></div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* DON'Ts Section */}
                      <div className={`p-5 rounded-lg shadow-lg border border-gray-200 flex flex-col transition-all duration-300 ${isAnalyzing ? 'opacity-60' : ''}`} style={{ backgroundColor: '#fef2f2', flex: '1' }}>
                        <div className="flex items-center gap-2 mb-3">
                          <X className="w-5 h-5 text-red-600" />
                          <h3 className="font-bold text-xl" style={{ color: PALETTE.gunmetal }}>DON'Ts {isCampaignMode && <span className="text-sm font-normal" style={{ color: `${PALETTE.gunmetal}60` }}>(Locked)</span>}</h3>
                        </div>
                        <div className="relative flex-grow">
                          <Textarea
                            placeholder={getPlaceholderContent('donts', transcriptLanguage)}
                            value={dontsText}
                            onChange={(e) => setDontsText(e.target.value)}
                            className="w-full h-full resize-none placeholder:text-gray-400 bg-transparent border-0 focus:ring-0 p-0 text-base leading-relaxed"
                            style={{ color: PALETTE.gunmetal }}
                            disabled={isAnalyzing || isCampaignMode}
                            onMouseEnter={(e) => {
                              const tooltip = e.currentTarget.nextElementSibling as HTMLElement;
                              if (tooltip) {
                                tooltip.style.opacity = '1';
                                setTimeout(() => {
                                  if (tooltip) tooltip.style.opacity = '0';
                                }, 2000);
                              }
                            }}
                          />
                          {/* Tooltip */}
                          <div className="absolute -top-16 left-1/2 transform -translate-x-1/2 opacity-0 transition-opacity duration-300 pointer-events-none z-20">
                            <div className="bg-white text-gray-800 text-xs rounded-lg py-2 px-3 text-center border border-gray-200 shadow-lg" style={{ minWidth: '300px' }}>
                              Separate multiple requirements with semicolons (;), periods (.), or new lines
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-white"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Progress Bar - Only show when analyzing */}
              {isAnalyzing && (
                <div className="w-full max-w-7xl mx-auto mt-4 sm:mt-6 px-4 sm:px-6">
                  <div className="flex justify-center">
                    <div className="w-full max-w-5xl"> {/* More spacious responsive progress bar */}
                      <div className="w-full">
                        <div className="flex justify-between items-center mb-4">
                          <span className="text-sm sm:text-base font-semibold" style={{ color: PALETTE.gunmetal }}>
                            Checking the Video if it complies with your brand guidelines...
                          </span>
                          <span className="text-base font-bold" style={{ color: PALETTE.gunmetal }}>
                            {progress}%
                          </span>
                        </div>
                        <div 
                          className="w-full rounded-full h-3 relative overflow-hidden"
                          style={{ background: '#E5E7EB' }}
                        >
                          <div 
                            className="h-3 rounded-full transition-all duration-500 ease-out relative"
                            style={{ 
                              width: `${progress}%`, 
                              background: `linear-gradient(90deg, ${PALETTE.inchworm}, #A3E635)`,
                              boxShadow: `0 0 12px rgba(177, 250, 99, 0.5)`
                            }}
                          >
                            {/* Animated shine effect */}
                            <div 
                              className="absolute inset-0 rounded-full opacity-30"
                              style={{
                                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)',
                                animation: progress > 0 && progress < 100 ? 'shine 2s infinite' : 'none'
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Bottom Navigation for Step 2 - REMOVED and replaced by fixed buttons */}
            </form>
          )}



                  {/* Step 3: Results - This will be handled by navigation to /result/{unique_id} */}

          {/* Step 4: Share */}
          {step === 4 && analysisResult && (
            <div className="w-full mx-auto px-4 sm:px-6">
              <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-start justify-center max-w-7xl mx-auto">
                {/* Left side - Video (Compact) */}
                <div className="w-full max-w-xs lg:max-w-[280px] xl:max-w-[320px] mx-auto lg:mx-0 flex-shrink-0"> {/* Professional video size */}
                  {videoUrl && (
                    <div className={`relative ${isVideoFullscreen ? 'video-disabled' : ''}`}>
                      <video 
                        key={videoUrl}
                        ref={videoRef}
                        src={videoUrl}
                        controls={!isVideoFullscreen}
                        className="w-full rounded-lg cursor-pointer aspect-[9/16]" 
                        style={{ objectFit: 'cover' }}
                        crossOrigin="anonymous"
                        lang="en"
                        onClick={handleVideoFullscreenOpen}
                        muted={isVideoFullscreen}
                      >
                        Your browser does not support the video tag.
                      </video>
                    </div>
                  )}
                </div>

                {/* Right side - Share Content (Primary Content) */}
                <div className="w-full flex-1 max-w-4xl lg:max-w-none space-y-6">
                  <div className="text-center mb-8">
                    <h2 className="text-2xl font-bold mb-4" style={{ color: PALETTE.gunmetal }}>
                      Share Your Results
                    </h2>
                    <p className="text-lg" style={{ color: PALETTE.gunmetal, opacity: 0.8 }}>
                      Share your analysis results with your team or clients.
                    </p>
                  </div>

                  {/* Share options placeholder */}
                  <div className="text-center">
                    <p className="text-lg mb-4" style={{ color: PALETTE.gunmetal }}>
                      Share options will be available here.
                    </p>
                    <p className="text-sm" style={{ color: PALETTE.gunmetal, opacity: 0.7 }}>
                      This step can include sharing via email, generating reports, or exporting data.
                    </p>
                  </div>
                </div>
              </div>

              {/* Bottom Navigation */}
              <div className="flex justify-between">
                <Button
                  type="button"
                  onClick={() => {
                    if (analysisResult?.unique_id) {
                      navigate(`${getBasePath()}/result/${analysisResult.unique_id}`);
                    }
                  }}
                  variant="outline"
                  className="text-base px-5 py-2"
                  style={{ borderColor: PALETTE.americanSilver, color: PALETTE.gunmetal }}
                >
                  View Full Results
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    // Reset to step 0 for new analysis
                    setStep(0);
                    setAnalysisResult(null);
                    setProgress(0);
                  }}
                  className="text-base px-5 py-2"
                  style={{ background: PALETTE.orange, color: PALETTE.brightSnow }}
                >
                  New Analysis
                </Button>
              </div>
            </div>
          )}
          {/* Fullscreen Video Modal */}
          {isVideoFullscreen && videoUrl && (
            <div 
              className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center p-4 fullscreen-modal"
              onClick={handleVideoFullscreenClose}
            >
              <div 
                className="relative max-w-full max-h-full"
                onClick={(e) => e.stopPropagation()}
              >
                <video
                  ref={fullscreenVideoRef}
                  src={videoUrl}
                  controls
                  className="rounded-lg"
                  style={{ 
                    aspectRatio: '9/16',
                    objectFit: 'contain',
                    maxHeight: '90vh',
                    maxWidth: '90vw',
                    width: 'auto',
                    height: 'auto'
                  }}
                  playsInline
                  preload="metadata"
                  lang="en"
                  onLoadStart={handleModalVideoLoad}
                  onLoadedData={handleModalVideoLoad}
                  onPlay={handleModalVideoPlay}
                  onPlaying={handleModalVideoPlay}
                >
                  Your browser does not support the video tag.
                </video>
                <Button
                  onClick={handleVideoFullscreenClose}
                  variant="ghost"
                  size="sm"
                  className="absolute top-2 right-2 p-2 bg-black bg-opacity-50 text-white hover:bg-opacity-70"
                >
                  <X size={20} />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Bottom Navigation for Steps 0 and 1 - REMOVED and replaced by fixed buttons */}
      </div>
      </div>
      
      {/* FIXED NAVIGATION BUTTONS */}
      {!isCampaignMode && (
        <div className="fixed bottom-8 left-0 right-0 z-20 flex justify-center">
          <div 
            className="flex items-center justify-center"
            style={{ 
              transform: isCampaignMode ? 'translateX(0)' : (sidebarOpen ? 'translateX(132px)' : 'translateX(28px)')
            }}
          >
            <div className={`flex ${(step > 0 && step < 2) || step === 2 ? 'gap-4' : ''}`}>
            {step === 0 && file && (
              <>
                <Button
                  type="button"
                  onClick={prevStep}
                  className="rounded-lg px-6 h-12 flex items-center justify-center shadow-lg gap-2"
                  style={{ 
                    background: PALETTE.brightSnow,
                    color: PALETTE.gunmetal,
                    border: `1px solid ${PALETTE.americanSilver}`
                  }}
                  aria-label="Previous Step"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span className="text-base font-semibold">Back to Campaign Details</span>
                </Button>
                <Button
                  type="button"
                  onClick={nextStep}
                  className="rounded-lg px-6 h-12 flex items-center justify-center shadow-lg gap-2"
                  style={{ 
                    background: PALETTE.inchworm,
                    color: PALETTE.gunmetal
                  }}
                  aria-label="Next Step"
                >
                  <CheckCircle className="h-4 w-4" />
                  <span className="text-base font-semibold">Check Brand Compliance</span>
                </Button>
              </>
            )}
            {step > 0 && (
              <Button
                  type="button"
                  onClick={prevStep}
                  className={`${step === 1 || step === 2 ? 'rounded-lg px-6 h-12' : 'rounded-lg h-12 w-12'} flex items-center justify-center shadow-lg gap-2`}
                  style={{ 
                    background: PALETTE.brightSnow,
                    color: PALETTE.gunmetal,
                    border: `1px solid ${PALETTE.americanSilver}`
                  }}
                  disabled={isAnalyzing}
                  aria-label="Previous Step"
              >
                  {step === 1 ? (
                    <>
                      <ArrowLeft className="h-4 w-4" />
                      <span className="text-base font-semibold">Back to Campaign Details</span>
                    </>
                  ) : step === 2 ? (
                    <>
                      <ArrowLeft className="h-4 w-4" />
                      <span className="text-base font-semibold">Back to Campaign Details</span>
                    </>
                  ) : (
                    <ArrowLeft className="h-5 w-5" />
                  )}
              </Button>
            )}
            {step < 2 && step > 0 && (
              <Button
                  type="button"
                  onClick={nextStep}
                  className="rounded-lg px-6 h-12 flex items-center justify-center shadow-lg gap-2"
                  style={{ background: PALETTE.inchworm, color: PALETTE.gunmetal }}
                  disabled={!file || (step === 1 && (!brandName || !exactProduct))}
                  aria-label="Next Step"
              >
                  <ArrowRight className="h-4 w-4" />
                  <span className="text-base font-semibold dos-donts-button">DOs & DON'Ts</span>
              </Button>
            )}
            {step === 2 && (
              <Button
                type="button"
                onClick={() => formRef.current?.requestSubmit()}
                className="rounded-lg px-8 h-12 flex items-center justify-center shadow-lg text-base font-semibold transition-all duration-300 gap-2"
                style={{
                  background: isAnalyzing
                    ? PALETTE.americanSilver
                    : PALETTE.inchworm,
                  border: 'none',
                  cursor: isAnalyzing ? 'not-allowed' : 'pointer',
                  color: PALETTE.gunmetal,
                }}
                disabled={isAnalyzing}
                aria-label="Compliance Check"
              >
                <CheckCircle className="h-4 w-4" />
                <span className="compliance-check-button">{isAnalyzing ? 'Analyzing...' : 'Analyze Video'}</span>
              </Button>
            )}
          </div>
          </div>
        </div>
      )}
      
      {/* Campaign Mode - Simple Analyze Button */}
      {isCampaignMode && file && (
        <div className="fixed bottom-8 left-0 right-0 z-20 flex flex-col items-center gap-2">
          {!creatorEmail && (
            <p className="text-sm" style={{ color: PALETTE.orange }}>
              Please enter your email above before analyzing
            </p>
          )}
          <div className="flex items-center justify-center gap-4">
            {/* Back to Contact Information Button */}
            <Button
              type="button"
              onClick={() => {
                if (campaignUuid) {
                  navigate(`/upload/${campaignUuid}`);
                }
              }}
              disabled={isAnalyzing}
              className="rounded-full px-6 h-12 flex items-center justify-center shadow-lg text-base font-semibold transition-all duration-300 gap-2"
              style={{
                background: PALETTE.brightSnow,
                border: `1px solid ${PALETTE.americanSilver}`,
                cursor: isAnalyzing ? 'not-allowed' : 'pointer',
                color: PALETTE.gunmetal,
                borderRadius: '9999px'
              }}
              aria-label="Back to Contact Information"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back</span>
            </Button>
            
            {/* Check Brand Compliance Button */}
            <Button
              type="button"
              onClick={() => analyzeVideo()}
              disabled={isAnalyzing || !creatorEmail}
              className="rounded-full px-8 h-12 flex items-center justify-center shadow-lg text-base font-semibold transition-all duration-300 gap-2"
              style={{
                background: (isAnalyzing || !creatorEmail) ? PALETTE.americanSilver : PALETTE.inchworm,
                border: 'none',
                cursor: (isAnalyzing || !creatorEmail) ? 'not-allowed' : 'pointer',
                color: PALETTE.gunmetal,
                borderRadius: '9999px'
              }}
              aria-label="Analyze Video"
            >
              <CheckCircle className="h-4 w-4" />
              <span>{isAnalyzing ? 'Analyzing...' : 'Check Brand Compliance'}</span>
            </Button>
          </div>
        </div>
      )}
      
      <style>{`
        /* Fullscreen video styles */
        video:fullscreen {
          object-fit: contain !important;
          aspect-ratio: 9/16 !important;
          width: auto !important;
          height: 100vh !important;
        }
        video:-webkit-full-screen {
          object-fit: contain !important;
          aspect-ratio: 9/16 !important;
          width: auto !important;
          height: 100vh !important;
        }
        video:-moz-full-screen {
          object-fit: contain !important;
          aspect-ratio: 9/16 !important;
          width: auto !important;
          height: 100vh !important;
        }
        
        /* Modal video styles */
        .fullscreen-modal video {
          aspect-ratio: 9/16 !important;
          object-fit: contain !important;
          max-height: 90vh !important;
          max-width: 90vw !important;
          width: auto !important;
          height: auto !important;
        }
        
        /* Prevent background video interaction when modal is open */
        .video-disabled {
          pointer-events: none !important;
          opacity: 0.5 !important;
        }
        
        /* Progress bar shine animation */
        @keyframes shine {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        /* Shepherd tour custom button styles */
        .shepherd-custom-buttons .shepherd-button {
          background-color: #B1FA63 !important;
          color: #243837 !important;
          border: 1px solid #B1FA63 !important;
          border-radius: 6px !important;
          padding: 8px 16px !important;
          font-weight: 500 !important;
          transition: all 0.2s ease !important;
        }
        
        .shepherd-custom-buttons .shepherd-button:hover {
          background-color: #A3E635 !important;
          border-color: #A3E635 !important;
          transform: translateY(-1px) !important;
        }
        
        .shepherd-custom-buttons .shepherd-button:focus {
          outline: none !important;
          box-shadow: 0 0 0 2px rgba(177, 250, 99, 0.3) !important;
        }
      `}</style>

      {/* Project Modal */}
      {!isCampaignMode && (
        <ProjectModal
          isOpen={isProjectModalOpen}
          onClose={() => setIsProjectModalOpen(false)}
          onSuccess={async (project) => {
            // Refresh projects list and select the new project
            try {
              const projectsData = await projectAPI.list();
              setProjects(projectsData);
              setSelectedProjectId(project.id);
            } catch (error) {
              console.error("Error refreshing projects:", error);
            }
          }}
          project={null}
          mode="create"
        />
      )}
      
        {/* Powered By - Bottom Right */}
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-4">
          <span className="text-sm" style={{ color: `${PALETTE.gunmetal}60` }}>
            powered by{' '}
            <a 
              href="https://brandcomply.ai" 
              target="_blank" 
              rel="noopener noreferrer"
              className="font-semibold hover:underline"
              style={{ color: PALETTE.gunmetal }}
            >
              BrandComply
            </a>
          </span>
        </div>

      {/* Tour styles for campaign mode */}
      {isCampaignMode && <style>{tourStyles}</style>}
    </div>
  );
};

export default Index;